import classNames from 'classnames';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Table from '../../../components/organisms/Table';
import Select from '../../../components/widgets/Select';
import TableCell from '../../../components/widgets/TableCell';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import {
    ATTRIBUTION_TAB_IDS,
    TRANSLATE_PREFIX,
    URL_QUERY_KEY_REGEX,
    buildPassthroughAdnetworkMacro,
    getDefaultMacroKey,
    getPostbackSingularMacros,
    isPostbackMacroDisallowedForCell,
    trackingLinkKeyFromMacro,
} from '../../consts';
import { resolveTemplateEntries } from '../../templateEntries';
import DropdownOptionLabel from '../DropdownOptionLabel';
import { useTabUpdater } from '../../formUpdaters';
import sharedTableCss from '../formTable.css';
import ParameterKeyCell from './ParameterKeyCell';
import css from './PostbackTemplateMacroTable.css';
import SingularMacroCell from './SingularMacroCell';
import { macroDescriptionKey, macroForTranslationKeys, macroLabelKey } from './consts';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;

const PostbackTemplateMacroTable = ({ eventType, platform, readOnly = false, translate }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const postbackSingularMacros = useMemo(() => getPostbackSingularMacros(postbackMacroNetworkName, platform), [
        postbackMacroNetworkName,
        platform,
    ]);
    const postbackSingularMacroSet = useMemo(() => new Set(postbackSingularMacros), [postbackSingularMacros]);
    const savedRows = useMemo(() => values.formData?.[TAB_ID]?.[eventType]?.[platform] || [], [
        values.formData,
        eventType,
        platform,
    ]);

    const trackingLinkParameters = useMemo(
        () => values.formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]?.tracking_link_parameters || [],
        [values.formData]
    );

    const platformTemplate = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const templateEntriesByMacro = useMemo(() => {
        const map = {};
        resolveTemplateEntries(platformTemplate, eventType, platform, postbackMacroNetworkName).forEach(
            ({ resolvedMacro, key, required }) => {
                map[resolvedMacro] = { key, required };
            }
        );
        return map;
    }, [platformTemplate, eventType, platform, postbackMacroNetworkName]);
    const templateLabel = platformTemplate
        ? translate(`${TRANSLATE_PREFIX}.PLATFORM_TEMPLATES.${platformTemplate.toUpperCase()}`)
        : '';

    // Mutators read prev rows via setValues(prev => ...) so concurrent edits to the same
    // event/platform table can't overwrite each other through a stale closure.
    const updateRows = updater =>
        updateTab(prevTab => {
            const prevEvent = prevTab[eventType] || {};
            const prevRows = prevEvent[platform] || [];
            return {
                ...prevTab,
                [eventType]: {
                    ...prevEvent,
                    [platform]: updater(prevRows),
                },
            };
        });

    const updateKey = (macro, key) => {
        updateRows(prevRows => {
            const idx = prevRows.findIndex(r => r.singular_macro === macro);
            const next = prevRows.slice();
            if (idx >= 0) {
                next[idx] = { ...next[idx], partner_parameter_key: key };
            } else {
                next.push({ singular_macro: macro, partner_parameter_key: key });
            }
            return next;
        });
    };

    const removeMacro = macro => updateRows(prevRows => prevRows.filter(r => r.singular_macro !== macro));

    const addMacro = macro => {
        updateRows(prevRows => [...prevRows, { singular_macro: macro, partner_parameter_key: '' }]);
    };

    const displayRows = useMemo(
        () =>
            savedRows.map(row => ({
                singular_macro: row.singular_macro,
                partner_parameter_key: row.partner_parameter_key || '',
            })),
        [savedRows]
    );

    const usedMacros = new Set(displayRows.map(r => r.singular_macro));
    // Any tracking-link row (custom or standard) whose singular_key is set contributes a
    // passthrough macro `{key?network}`. partner_macro state doesn't gate the enum — the row's
    // existence is what makes the macro a valid choice. The row deletion is what removes it.
    const passthroughMacros = [
        ...new Set(
            trackingLinkParameters
                .filter(p => !!p.singular_key?.trim())
                .map(p => buildPassthroughAdnetworkMacro(p.singular_key.trim(), postbackMacroNetworkName))
        ),
    ];
    const trackingParamForMacro = m => {
        const key = trackingLinkKeyFromMacro(m);
        if (!key) return null;
        return trackingLinkParameters.find(p => (p.singular_key || '').trim() === key) || null;
    };
    const standardTrackingParamLabelKey = key =>
        `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB.PARAMS.${(key || '').toUpperCase()}.FIELD_NAME`;
    const standardTrackingParamDescriptionKey = key =>
        `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB.PARAMS.${(key || '').toUpperCase()}.DESCRIPTION`;
    // Macros forbidden for this specific (template, event_type) cell are filtered out of both dropdown groups.
    const isCellAllowedMacro = m => !isPostbackMacroDisallowedForCell(platformTemplate, eventType, m);
    const passthroughOptions = passthroughMacros
        .filter(m => !usedMacros.has(m) && !postbackSingularMacroSet.has(m) && isCellAllowedMacro(m))
        .map(m => {
            const param = trackingParamForMacro(m);
            if (param?.is_custom) {
                return {
                    value: m,
                    label: translate(`${TAB_PREFIX}.MACRO_LABELS.CUSTOM_TRACKING_LINK_MACRO`),
                };
            }
            // Standard tracking-link row: use the canonical FIELD_NAME translation as the
            // dropdown's mini-description (matches what the tracking link table shows).
            const key = trackingLinkKeyFromMacro(m) || '';
            const labelKey = standardTrackingParamLabelKey(key);
            const label = translate(labelKey);
            return { value: m, label: label === labelKey ? key : label };
        });
    const standardOptions = postbackSingularMacros
        .filter(m => !usedMacros.has(m) && isCellAllowedMacro(m))
        .map(m => ({
            value: m,
            label: translate(macroLabelKey(macroForTranslationKeys(m, postbackMacroNetworkName))),
        }));
    const divider = { value: '__divider__', label: '', isDivider: true, isDisabled: true };
    const dropdownOptions =
        standardOptions.length && passthroughOptions.length
            ? [...standardOptions, divider, ...passthroughOptions]
            : [...standardOptions, ...passthroughOptions];

    const tableMetadata = [
        {
            name: 'singular_macro',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.SINGULAR_MACRO`,
            cellComponent: SingularMacroCell,
            cellProps: { onRemove: removeMacro, readOnly, translate },
        },
        {
            name: 'description',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.DESCRIPTION`,
            cellComponent: TableCell,
            headerWidth: '50%',
            cellProps: { textClassName: css.descriptionCell },
        },
        {
            name: 'parameter_key',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.TRACKING_LINK_PARAMETER_KEY`,
            cellComponent: ParameterKeyCell,
            cellProps: { onChange: updateKey, readOnly, translate },
        },
    ];

    const describeMacro = macro => {
        if (postbackSingularMacroSet.has(macro)) {
            return translate(macroDescriptionKey(macroForTranslationKeys(macro, postbackMacroNetworkName))) || '';
        }
        const tk = trackingLinkKeyFromMacro(macro);
        if (!tk) return '';
        const param = trackingLinkParameters.find(p => (p.singular_key || '').trim() === tk);
        if (!param) return '';
        // Custom rows: use the description the user typed in tracking link options.
        // Standard rows: use the canonical DESCRIPTION translation, falling back to whatever the
        // tracking-link row has if the translation isn't found.
        if (param.is_custom) return param.description || '';
        const descKey = standardTrackingParamDescriptionKey(tk);
        const desc = translate(descKey);
        return desc === descKey ? param.description || '' : desc;
    };

    const partnerKeyCounts = displayRows.reduce((acc, r) => {
        const k = r.partner_parameter_key?.trim();
        if (k) acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    const tableData = displayRows.map(row => {
        const partnerKey = row.partner_parameter_key || '';
        const paramErrorKey = !URL_QUERY_KEY_REGEX.test(partnerKey)
            ? 'POSTBACK_TEMPLATE_PARAM_KEY_INVALID_CHARS'
            : null;
        const trimmedKey = partnerKey.trim();
        const duplicateKey = !readOnly && !!trimmedKey && partnerKeyCounts[trimmedKey] > 1;
        const templateEntry = templateEntriesByMacro[row.singular_macro];
        const isAutoFilled = !!partnerKey && templateEntry?.key === partnerKey;
        const isRequired = !!templateEntry?.required;
        return {
            key: row.singular_macro,
            singular_macro: row.singular_macro,
            description: describeMacro(row.singular_macro),
            partner_parameter_key: row.partner_parameter_key,
            paramErrorKey,
            duplicateKey,
            isAutoFilled,
            isRequired,
            requiredByTemplateLabel: templateLabel,
            templateKey: templateEntry?.key,
            defaultKey: getDefaultMacroKey(row.singular_macro),
        };
    });

    // Required rows can't be deleted — surface them at the top, preserving relative order
    // within each group (Array.prototype.sort is stable in ES2019+).
    const sortedTableData = [...tableData].sort((a, b) => Number(b.isRequired) - Number(a.isRequired));

    return (
        <div className={classNames(css.tableWrapper)}>
            <Table
                metadata={tableMetadata}
                data={sortedTableData}
                zebraTable={false}
                rowHoverEnabled={false}
                showInnerBorder={false}
                tableClass={sharedTableCss.table}
                theadClass={sharedTableCss.thead}
                thClass={sharedTableCss.th}
                trClass={sharedTableCss.row}
            />
            {!readOnly && (
                <div className={css.addRow}>
                    <Select
                        options={dropdownOptions}
                        value={null}
                        onChange={opt => opt && addMacro(opt.value)}
                        placeholder={translate(`${TAB_PREFIX}.ADD_MACRO_PLACEHOLDER`)}
                        isSearchable
                        isClearable={false}
                        isDisabled={!dropdownOptions.length}
                        className={css.addMacroSelect}
                        formatOptionLabel={DropdownOptionLabel}
                        classNames={{
                            option: ({ data }) => (data?.isDivider ? css.dividerOption : ''),
                        }}
                        usePortal
                    />
                </div>
            )}
        </div>
    );
};

PostbackTemplateMacroTable.propTypes = {
    eventType: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(PostbackTemplateMacroTable);
